import api from './api';
import { API_BASE } from '../utils/apiBase';

const API_URL = API_BASE;

export const authService = {
  /**
   * Register new user
   */
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  /**
   * Login user
   * Sets token in localStorage for mobile compatibility
   * Server also sets httpOnly cookies
   */
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials, {
      withCredentials: true // Enable cookies
    });
    
    if (response.data.token) {
      // Store token for mobile apps / fallback
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      // Native apps store the refresh token too (the httpOnly cookie isn't
      // reliable in WKWebView) so /refresh can replay it from the body.
      if (response.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.refreshToken);
      }
    }
    return response.data;
  },

  /**
   * Logout user
   * Clears local storage and server-side cookies
   */
  logout: async () => {
    try {
      await api.post('/auth/logout', {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  /**
   * Refresh access token
   */
  refreshToken: async () => {
    try {
      const response = await api.post('/auth/refresh', {}, { withCredentials: true });
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      return response.data;
    } catch (error) {
      // If refresh fails, clear auth state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      throw error;
    }
  },

  /**
   * Get current authenticated user from server
   */
  getCurrentUser: async () => {
    const response = await api.get('/auth/me', { withCredentials: true });
    return response.data;
  },

  /**
   * Check authentication status
   */
  checkAuth: async () => {
    try {
      const response = await api.get('/auth/check', { withCredentials: true });
      return response.data;
    } catch (error) {
      return { isAuthenticated: false, role: 'visitor', user: null };
    }
  },

  /**
   * Request password reset
   */
  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  /**
   * Reset password with token
   */
  resetPassword: async (token, newPassword) => {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },

  /**
   * Verify email with token
   */
  verifyEmail: async (token) => {
    const response = await api.post('/auth/verify-email', { token });
    return response.data;
  },

  /**
   * Check if user is authenticated (local check)
   */
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  /**
   * Get stored user from localStorage
   */
  getStoredUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  /**
   * Get stored token
   */
  getToken: () => {
    return localStorage.getItem('token');
  },

  /**
   * Update stored user
   */
  updateStoredUser: (user) => {
    localStorage.setItem('user', JSON.stringify(user));
  }
};

export default authService;
